import type { ReactNode } from 'react';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import { Divider, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';

import type { Subjects } from '~/contexts/ability';
import { routes } from '~/constants/index';
import { useAbility } from '~/contexts/ability';
import { useLanguage } from '~/hooks/language';

interface NavigationItem {
  section?: string;
  routes: { route: string; label: string; scope?: Subjects }[];
}
const NAVIGATION = [
  { routes: [{ route: routes.TOURNAMENTS, label: 'general.tournaments', scope: 'Tournaments' satisfies Subjects }] },
  { routes: [{ route: routes.PLAYERS, label: 'general.players', scope: 'Players' satisfies Subjects }] },
  { routes: [{ route: routes.GAMES, label: 'general.games', scope: 'Games' satisfies Subjects }] },
  { routes: [{ route: routes.TEAMS, label: 'general.teams', scope: 'Teams' satisfies Subjects }] },
  { routes: [{ route: routes.REFEREES, label: 'general.referees', scope: 'Referees' satisfies Subjects }] },
  {
    section: 'navigation.settings',
    routes: [
      { route: routes.SETTINGS_ADMINS, label: 'general.admins', scope: 'Admins' satisfies Subjects },
      { route: routes.SETTINGS_GROUPS, label: 'general.groups', scope: 'Groups' satisfies Subjects },
      { route: routes.SETTINGS_POSITIONS, label: 'general.positions', scope: 'Positions' satisfies Subjects },
      { route: routes.SETTINGS_TOUR_TYPES, label: 'general.tourTypes', scope: 'TourTypes' satisfies Subjects },
    ],
  },
] satisfies NavigationItem[];

const ROUTES = [
  { path: routes.TOURNAMENTS },
  { path: routes.TOURNAMENT },
  { path: routes.TOURNAMENT_SCHEDULE },
  { path: routes.GAMES },
  { path: routes.TOURNAMENT_TEAMS },
  { path: routes.TOURNAMENTS_LIST },
  { path: routes.PLAYERS },
  { path: routes.REFEREES },
  { path: routes.TEAMS },
  { path: routes.SETTINGS_ADMINS },
  { path: routes.SETTINGS_GROUPS },
  { path: routes.SETTINGS_POSITIONS },
  { path: routes.SETTINGS_TOUR_TYPES },
];

const Navigation = () => {
  const location = useLocation();
  const { translate } = useLanguage('ua');
  const ability = useAbility();
  const currentRoute = matchRoutes(ROUTES, location)?.[0].route.path;
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <List sx={{ color: theme.palette.primary[100] }}>
      {NAVIGATION.map(section => {
        const result: ReactNode[] = [];

        if (section.section) {
          result.push(<Divider key={`${section.section}-divider`} sx={{ mt: '20px', mb: '30px' }} />);
          result.push(
            <Typography key={section.section} variant="h6" sx={{ m: '16px 10px 10px 16px', opacity: 0.5 }}>
              {translate(section.section)}
            </Typography>
          );
        }

        result.push(
          ...section.routes.map(route => {
            const isMatched = currentRoute?.includes(route.route);
            const bgColor = theme.palette.mode === 'dark' ? theme.palette.primary[200] : theme.palette.secondary[500];
            const bgColorHover =
              theme.palette.mode === 'dark' ? theme.palette.primary[100] : theme.palette.secondary[600];

            if ('scope' in route && !ability.can('read', route.scope)) {
              return null;
            }

            return (
              <ListItem key={`${section.section}-${route.route}-route`} disablePadding>
                <ListItemButton
                  sx={{
                    backgroundColor: isMatched ? bgColor : 'transparent',
                    color: isMatched ? theme.palette.secondary[900] : 'inherit',
                    '&:hover': { backgroundColor: isMatched ? bgColorHover : '' },
                  }}
                  onClick={() => {
                    navigate(route.route);
                  }}
                >
                  <ListItemText
                    disableTypography
                    primary={<Typography fontWeight="bold">{translate(route.label)}</Typography>}
                  />
                  {isMatched && <ChevronRightOutlined sx={{ ml: 'auto' }} />}
                </ListItemButton>
              </ListItem>
            );
          })
        );

        return result;
      })}
    </List>
  );
};

export default Navigation;
